import type {
  ButtonHTMLAttributes,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  ReactNode,
} from "react";

// Core component library — maps 1:1 to the documented specs in
// design-system/system.md (.btn / .chip / .card / .field / .label / .section-title).
// Styling via Tailwind utilities backed by the design tokens.

type ButtonVariant = "primary" | "accent" | "ghost" | "danger";

const BTN_BASE =
  "inline-flex items-center justify-center gap-1.5 rounded-pill px-5 py-2.5 text-sm font-semibold min-h-[44px] transition-transform duration-100 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none";

const BTN_VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-ink text-paper",
  accent: "bg-accent text-white",
  ghost: "bg-transparent text-ink-2 border border-slate-300",
  danger: "bg-crit text-white",
};

export function Button({
  variant = "primary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }) {
  return <button className={`${BTN_BASE} ${BTN_VARIANT[variant]} ${className}`} {...props} />;
}

type ChipVariant = "ok" | "warn" | "dim" | "accent" | "ink";

const CHIP_VARIANT: Record<ChipVariant, string> = {
  ok: "bg-good-50 text-good",
  warn: "bg-warn-50 text-warn",
  dim: "bg-paper-3 text-slate-500",
  accent: "bg-accent-50 text-accent-ink",
  ink: "bg-ink text-paper",
};

export function Chip({ variant = "dim", children }: { variant?: ChipVariant; children: ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.1px] ${CHIP_VARIANT[variant]}`}
    >
      {children}
    </span>
  );
}

export function Card({ className = "", children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`bg-cream border border-hair rounded-lg p-5 shadow-1 ${className}`}>{children}</div>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-slate-500 mb-1.5">
      {children}
    </div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <div className="font-mono text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-500 mb-3">
      {children}
    </div>
  );
}

export function Field({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`w-full bg-paper-2 border border-hair rounded-sm px-3.5 py-2.5 text-sm text-ink font-sans outline-none focus:border-accent ${className}`}
      {...props}
    />
  );
}

export function Textarea({ className = "", ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      className={`w-full bg-paper-2 border border-hair rounded-sm px-3.5 py-2.5 text-sm text-ink font-sans outline-none focus:border-accent resize-y ${className}`}
      {...props}
    />
  );
}

type AlertVariant = "error" | "warn" | "ok";

const ALERT_VARIANT: Record<AlertVariant, string> = {
  error: "bg-[#f7e9e6] text-crit border-[#e6c3bb]",
  warn: "bg-warn-50 text-warn border-[#e3cd97]",
  ok: "bg-good-50 text-good border-[#bcd9c9]",
};

export function Alert({
  variant = "error",
  children,
  className = "",
}: {
  variant?: AlertVariant;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={variant === "error" ? "alert" : "status"}
      className={`rounded-sm border px-3.5 py-2.5 text-sm ${ALERT_VARIANT[variant]} ${className}`}
    >
      {children}
    </div>
  );
}

export function Spinner({ size = 18 }: { size?: number }) {
  return (
    <span
      className="inline-block rounded-full border-2 border-slate-200 border-t-accent animate-spin"
      style={{ width: size, height: size }}
      role="status"
      aria-label="Loading"
    />
  );
}
